<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonViewSwitcher from "@/components/ArgonViewSwitcher.vue";
import CopyValue from "@/components/CopyValue.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { taskStore } from "@/_store/taskStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";
import { courierList } from "@/maestros/courierValues";
import {
  formatCourier,
  formatHandlingType,
  formatTaskContentDescriptionTooltip,
  formatVolume,
  formatWeight,
  hasTaskContentDescription,
  // formatTaskDeliveryInfoSummary, hasTaskDeliveryInfoSummary: usadas por la
  // columna "Info reparto", comentada de momento (no aporta valor)
} from "@/utils/formatters";
import StopDetailModal from "@/views/plans/components/stop-detail/StopDetailModal.vue";
import TaskDetailModal from "@/views/bultos/components/TaskDetailModal.vue";
import BultoLocationMapCanvas from "@/views/bultos/components/BultoLocationMapCanvas.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";

const viewMode = ref("table");
const viewOptions = [
  { value: "table", icon: "fa fa-list", title: "Vista tabla" },
  { value: "map", icon: "fa fa-map-marker", title: "Vista mapa" },
];
const mapHistories = computed(() => tasksStore.locationHistories);
const mapLoading = ref(false);
const mapError = ref("");

const statusOptions = Object.entries(ESTATUS_ENUM.task).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);

const tableStore = argonTableStore();
const tasksStore = taskStore();
const userStores = userStore();
const snackbar = ref(null);
const showStopDetailModal = ref(false);
const showTaskDetailModal = ref(false);
const selectedStopId = ref(null);
const selectedTaskId = ref(null);
const argonTableRef = ref(null);

const fieldFilters = computed(() => [
  { field: "barcode", description: "Código de barras", exact: true },
  { field: "shipment_reference", description: "Referencia", exact: true },
  {
    field: viewMode.value === "map" ? "movements.stop_number" : "stop_number",
    description: "Número parada",
    exact: true,
  },
]);

const defaultFilters = ref([
  {
    field: "delivery_info.status",
    label: "Estado",
    multi: true,
    options: statusOptions,
  },
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: [
      { label: "Recogida", value: "RECOGIDA" },
      { label: "Entrega", value: "ENTREGA" },
    ],
  },
  {
    field: "courier",
    label: "Transportista",
    multi: false,
    options: courierList,
  },
]);

const tableItems = computed(() => tasksStore.tasks);

const columns = ref([
  { header: "Código de barras", field: "barcode", sort: false, position: "start" },
  { header: "Referencia", field: "shipment_reference", sort: false, position: "start" },
  { header: "Número parada", field: "stop_number", sort: false, position: "start" },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  { header: "Medidas", field: "measurements", sort: false, position: "start" },
  { header: "Peso", field: "weight", sort: false, position: "start" },
  { header: "Manipulación", field: "handling_type", sort: false, position: "start" },
  { header: "Naturaleza", field: "content_description", sort: false, position: "center" },
  { header: "Transportista", field: "courier", sort: false, position: "start" },
  // { header: "Info reparto", field: "delivery_info.summary", sort: false, position: "center" },
  { header: "Estado", field: "status", sort: false, position: "center" },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1" },
]);

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

const selectedStopRow = computed(() => {
  if (!Array.isArray(tableStore.checkedList) || tableStore.checkedList.length !== 1) {
    return null;
  }

  const rows = Array.isArray(tableItems.value?.data)
    ? tableItems.value.data
    : Array.isArray(tableItems.value)
      ? tableItems.value
      : [];

  return rows.find((item) => item?._id === tableStore.checkedList[0]) || null;
});

const canViewStop = computed(() => Boolean(selectedStopRow.value));
const viewStopTooltip = computed(() =>
  canViewStop.value ? "" : "Debe seleccionar una única parada."
);

function getTaskTypeLabel(taskItem) {
  const typeValue = String(taskItem?.type || "").toUpperCase();
  return `${typeValue.slice(0, 1)}${typeValue.slice(1).toLowerCase()}`;
}

function getTaskStatusTooltip(item) {
  const status = String(item?.delivery_info?.status || "").toUpperCase();
  const description = String(item?.delivery_info?.status_description || "").trim();

  if (status === "INCIDENCIA" && description) {
    return `Incidencia: ${description}`;
  }

  return "";
}

function openStopDetailModal() {
  if (!canViewStop.value) return;

  const stopId = selectedStopRow.value?.stop_id || "";
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontró el identificador de la parada para abrir el detalle.",
      type: "danger",
      color: "white",
    };
    return;
  }

  selectedStopId.value = stopId;
  showStopDetailModal.value = true;
}

function closeStopDetailModal() {
  showStopDetailModal.value = false;
  selectedStopId.value = null;
}

function openTaskDetailModal(taskItem) {
  const taskId = taskItem?._id ?? "";
  if (!taskId) return;
  selectedTaskId.value = String(taskId);
  showTaskDetailModal.value = true;
}

function closeTaskDetailModal() {
  showTaskDetailModal.value = false;
  selectedTaskId.value = null;
}

function openStopDetailForTask(taskItem) {
  const stopId = taskItem?.stop_id || "";
  if (!stopId) {
    snackbar.value = {
      title: "Error",
      message: "No se encontró el identificador de la parada para abrir el detalle.",
      type: "danger",
      color: "white",
    };
    return;
  }

  selectedStopId.value = stopId;
  showStopDetailModal.value = true;
}

function reloadPage() {
  userStores.filtersPage.bultos = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  tasksStore.getTasks(tableStore.pagination, tableStore.query, tableStore.sort);
}

function resetMapState() {
  mapError.value = "";
  mapLoading.value = false;
  tasksStore.locationHistories = null;
}

function onMapFilterChanged() {
  if (!tableStore.filters.length) {
    resetMapState();
    argonTableRef.value?.displaySpinner(false);
    return;
  }

  mapError.value = "";
  mapLoading.value = true;
  tasksStore
    .getLocationHistoryQl(tableStore.query, tableStore.sort)
    .then((result) => {
      if (!result?.data?.length) {
        mapError.value = "No se han encontrado bultos con ese criterio.";
      }
    })
    .catch((reason) => {
      const messages = errorUtils.getErrorMessages(reason);
      mapError.value = messages[0] || "No se ha podido cargar la ubicación de los bultos.";
    })
    .finally(() => {
      mapLoading.value = false;
      argonTableRef.value?.displaySpinner(false);
    });
}

function onFilterChanged() {
  if (viewMode.value === "map") {
    onMapFilterChanged();
    return;
  }
  reloadPage();
}

watch(viewMode, () => {
  tableStore.changeFilters([]);
  resetMapState();
});

async function exportTasks() {
  const bytes = await tasksStore.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(bytes, fileUtils.generateDownloadFileName("xlsx"));
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function closeSnackbar() {
  snackbar.value = null;
}

function getRowActionsMenuId(taskItem) {
  return `task-row-actions-${String(taskItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions(taskItem) {
  const hasStop = Boolean(taskItem?.stop_id);

  return [
    [{ label: "Detalle", event: "detail" }],
    [
      {
        label: "Ver parada",
        event: "viewStop",
        disabled: !hasStop,
        tooltip: hasStop ? "" : "El bulto no tiene parada asociada.",
      },
    ],
  ];
}

function onRowAction(taskItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !taskItem) return;

  if (actionEvent === "detail") {
    openTaskDetailModal(taskItem);
    return;
  }

  if (actionEvent === "viewStop") {
    openStopDetailForTask(taskItem);
  }
}

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.bultos?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

watch(
  () => tasksStore.error,
  (value) => {
    if (!value || viewMode.value === "map") return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card bultos-table-view" :class="{ 'is-map-mode': viewMode === 'map' }">
    <div class="card-body bultos-table-body">
      <argon-table
        v-if="tasksStore.tasks"
        ref="argonTableRef"
        :columns="columns"
        :items="tableItems"
        :field-filters="fieldFilters"
        :default-filters="viewMode === 'map' ? [] : defaultFilters"        :show-filters="viewMode !== 'map'"
        :show-pagination="viewMode !== 'map'"
        :show-new-button="false"
        :show-config-option="viewMode !== 'map'"
        :show-importar="false"
        :show-check-input="true"
        @filter-changed="onFilterChanged"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @on-click="openTaskDetailModal"
        @export-data="exportTasks"
      >
        <template #button-custom>
          <argon-view-switcher v-model="viewMode" :options="viewOptions" class="me-2" />
        </template>
        <template #buttons>
          <div class="dropdown d-inline" data-dropdown-static style="--dropdown-gap: 4px">
            <argon-button
              id="bultosActionsDropdown"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="bultosActionsDropdown"
            >
              <li v-tooltip="viewStopTooltip" :class="{ disabled: !canViewStop }">
                <a
                  class="dropdown-item border-radius-md"
                  :aria-disabled="!canViewStop"
                  @click.prevent="openStopDetailModal"
                >
                  Ver parada
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td :text-primary="item.barcode" :position="getPosition('barcode')">
            <template v-if="item.barcode" #append>
              <copy-value :value-to-copy="item.barcode" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            :text-primary="item.shipment_reference"
            :position="getPosition('shipment_reference')"
          >
            <template v-if="item.shipment_reference" #append>
              <copy-value :value-to-copy="item.shipment_reference" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            :text-primary="item.stop_number ?? ''"
            :position="getPosition('stop_number')"
          >
            <template v-if="item.stop_number" #append>
              <copy-value :value-to-copy="String(item.stop_number)" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td :position="getPosition('type')">
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="getTaskTypeLabel(item)"
            />
          </argon-td>
          <argon-td
            :text-primary="item.measurements"
            :position="getPosition('measurements')"
          />
          <argon-td
            :text-primary="formatWeight(item.weight)"
            :text-secondary="formatVolume(item.volumetric_weight, 3)"
            :position="getPosition('weight')"
          />
          <argon-td
            :text-primary="formatHandlingType(item.handling_type)"
            :position="getPosition('handling_type')"
          />
          <argon-td
            :position="getPosition('content_description')"
          >
            <i
              v-if="hasTaskContentDescription(item)"
              v-tooltip="formatTaskContentDescriptionTooltip(item)"
              class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
              aria-label="Ver naturaleza de la mercancía"
            />
          </argon-td>
          <argon-td
            :text-primary="formatCourier(item.courier)"
            :position="getPosition('courier')"
          />
          <!-- <argon-td
            :position="getPosition('delivery_info.summary')"
          >
            <i
              v-if="hasTaskDeliveryInfoSummary(item)"
              v-tooltip="formatTaskDeliveryInfoSummary(item)"
              class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
              aria-label="Ver info reparto"
            />
          </argon-td> -->
          <argon-td :position="getPosition('status')">
            <argon-status
              entity="task"
              :status="item.delivery_info?.status"
              :tooltip-external-text="getTaskStatusTooltip(item)"
              width="100px"
            />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions(item)"
                aria-label="Acciones del bulto"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
      <div v-show="viewMode === 'map'" class="bultos-map-wrap">
        <bulto-location-map-canvas
          :histories="mapHistories"
          :is-loading="mapLoading"
          :error-message="mapError"
        />
      </div>
    </div>
  </div>

  <stop-detail-modal
    v-if="showStopDetailModal"
    modal-id="bultos-stop-detail-modal"
    :stop-id="selectedStopId"
    @close="closeStopDetailModal"
  />

  <task-detail-modal
    v-if="showTaskDetailModal && selectedTaskId"
    modal-id="bultos-task-detail-modal"
    :task-id="selectedTaskId"
    @close="closeTaskDetailModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
:deep(.bultos-table-body thead th.text-center.w-1:last-child),
:deep(.bultos-table-body tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}

.is-map-mode :deep(#argon-table) {
  display: none !important;
}

.is-map-mode :deep(#argon-table-container) {
  flex: 0 0 auto;
  height: auto;
  max-height: none;
  overflow: visible;
}

.bultos-map-wrap {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 480px;
  padding: 8px 0 0;
}
</style>
